import { useAuthenticator } from '@aws-amplify/ui-react';
import { Box, Modal, Typography } from '@mui/material';
import { getCurrentUser } from 'aws-amplify/auth';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useThemeContext } from '../../contexts/ThemeContext';
import { handleUserSignOut } from '../../utils/user';
import { LAST_USER_ACTIVITY_KEY } from '../../constants/localStorageKeys';
import { colors } from '../../styles/fontStyles';
import PrimaryBlueButton from '../common/buttons/PrimaryBlueButton';
import BorderedButton from '../common/buttons/BorderedButton';
import { throttle } from '../../utils/helpers';

// Number of minutes to check before the Cognito token expires(60 minutes by default)
// which in fact last the same as the refresh token(30 days)
// so AWS amplify Cognito always tries to make sure the user session is active and if expired it will refresh it
// when using any of the AWS Amplify Auth methods
const TOKEN_DURATION_IN_MINUTES: number = parseInt(process.env.REACT_APP_TOKEN_DURATION_IN_MINUTES || '60');
const MINUTES_BEFORE_EXPIRING_TOKEN: number = parseInt(process.env.REACT_APP_MINUTES_BEFORE_EXPIRING_TOKEN || '5');

const INACTIVITY_WARNING_LIMIT = (TOKEN_DURATION_IN_MINUTES - MINUTES_BEFORE_EXPIRING_TOKEN) * 60 * 1000;
const TOKEN_DURATION_LIMIT = TOKEN_DURATION_IN_MINUTES * 60 * 1000;

const SessionInactivityWarningModal: React.FC = () => {
  const { handleLogOut, userType } = useThemeContext();
  const [showModal, setShowModal] = useState(false);
  const { signOut } = useAuthenticator((context) => [context.user]);
  const updateLastActivity = useCallback(() => {
    localStorage.setItem(LAST_USER_ACTIVITY_KEY, Date.now().toString());
  }, []);

  // if the user mouse is moving really fast, the event will be triggered only
  // once every 500ms
  const throttledUpdateLastActivityRef = useRef(throttle(updateLastActivity, 500));
  const onHandleLogOut = () => {
    handleLogOut();
    setShowModal(false);
  };

  const checkSession = useCallback(async () => {
    const lastActivity = localStorage.getItem(LAST_USER_ACTIVITY_KEY);

    // User just logged in
    if (!lastActivity) {
      updateLastActivity();
      return;
    }

    const currentTime = Date.now();
    const inactiveTime = currentTime - parseInt(lastActivity);

    if (inactiveTime > TOKEN_DURATION_LIMIT) {
      onHandleLogOut();
      return;
    }

    if (inactiveTime > INACTIVITY_WARNING_LIMIT) {
      setShowModal(true);
    }
  }, [updateLastActivity]);

  useEffect(() => {
    if (userType === 'unauthenticated') return;

    const intervalId = setInterval(checkSession, 1000);

    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'touchend', 'scroll'];
    // mousemove for desktop, touchmove for mobile, wheel for both
    // These events are throttled to avoid performance issues
    const throttledEvents = ['mousemove', 'touchmove', 'wheel'];

    activityEvents.forEach((event) => {
      window.addEventListener(event, updateLastActivity);
    });

    throttledEvents.forEach((event) => {
      window.addEventListener(event, throttledUpdateLastActivityRef.current);
    });

    return () => {
      clearInterval(intervalId);
      activityEvents.forEach((event) => {
        window.removeEventListener(event, updateLastActivity);
      });

      throttledEvents.forEach((event) => {
        window.removeEventListener(event, throttledUpdateLastActivityRef.current);
      });
    };
  }, [checkSession, updateLastActivity, userType]);

  const extendSession = async () => {
    try {
      // This will refresh the session if it's still valid
      await getCurrentUser();
      updateLastActivity();
      setShowModal(false);
    } catch (error) {
      console.error('Failed to extend session:', error);
      // Handle session extension failure (e.g., redirect to login)
      await signOut();
    }
  };

  const handlingOnCloseModal = (event: object, reason: string) => {
    if (reason !== 'backdropClick') {
      setShowModal(false);
    }
  };

  return (
    <Modal
      disableEscapeKeyDown
      open={showModal}
      onClose={handlingOnCloseModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          backgroundColor: colors.midnightBlue,
          borderRadius: '8px',
          maxWidth: '500px',
          width: '75%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          padding: '40px 24px',
          position: 'relative',
          border: 'none',
          outline: 'none',
        }}
      >
        <Typography
          id="modal-modal-title"
          variant="h5"
          sx={{
            color: colors.white,
            fontSize: '36px',
            fontWeight: 400,
            marginBottom: '36px',
          }}
        >
          Your session has been inactive. Would you like to extend it?
        </Typography>
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: { xs: 'center', sm: 'flex-start' },
            maxWidth: { xs: '100%', sm: 'auto' },
          }}
        >
          <PrimaryBlueButton onClick={extendSession} buttonText="Extend Session" />
          <BorderedButton
            sxProps={{
              height: '41px',
              marginLeft: { xs: '0', sm: '10px' },
              marginTop: { xs: '10px', sm: '0' },
            }}
            onClick={onHandleLogOut}
            buttonText={'Logout'}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default SessionInactivityWarningModal;
